// A member's Position Sizing starting values come only from the backend
// (`fetch_max_pos_size_and_normal_vol`); this module validates and formats them.
// Max Pos Size and Normal Vol are stored as fractions and shown as percents.

const STARTING_VALUE_FIELDS = Object.freeze([
    'max_pos_size',
    'normal_vol',
    'target_vol_pct',
    'target_vol_dollar',
    'scaler',
]);

// The per-category Sizing Targets fields a starting value seeds.
const TARGET_DEFAULT_FIELDS = Object.freeze([
    'target_vol_pct',
    'target_vol_dollar',
    'scaler',
]);

// The inputs in the order both dialogs show them.
export const STARTING_VALUE_INPUTS = Object.freeze([
    { field: 'max_pos_size', label: 'Max Pos Size (%)', percent: true },
    { field: 'normal_vol', label: 'Normal Vol (%)', percent: true },
    { field: 'scaler', label: 'Scaler' },
    { field: 'target_vol_pct', label: 'Target Vol (%)' },
    { field: 'target_vol_dollar', label: 'Target Vol ($)', grouped: true },
]);

const INPUT_BY_FIELD = Object.freeze(Object.fromEntries(
    STARTING_VALUE_INPUTS.map((input) => [input.field, input]),
));
// The settings a Target Vol ($) suggestion depends on.
export const SUGGESTION_BASIS_FIELDS = Object.freeze([
    'max_pos_size',
    'normal_vol',
    'target_vol_pct',
    'scaler',
]);

const isPositiveFinite = (value) => (
    typeof value === 'number' && Number.isFinite(value) && value > 0
);

function readValues(raw, fields = STARTING_VALUE_FIELDS) {
    if (!raw || typeof raw !== 'object') return null;
    const values = {};
    for (const field of fields) {
        if (!isPositiveFinite(raw[field])) return null;
        values[field] = raw[field];
    }
    return values;
}

/** Validate the starting-values block shared by the read and save routes. */
export function normalizePosSizingStartingValues(response) {
    const current = readValues(response?.starting_values);
    const initial = readValues(response?.initial_starting_values);
    const version = response?.starting_values_version;
    if (
        !current
        || !initial
        || typeof response?.starting_values_chosen !== 'boolean'
        || !Number.isInteger(version)
        || version < 0
    ) {
        return null;
    }
    return {
        current,
        initial,
        chosen: response.starting_values_chosen,
        version,
    };
}

export function pickStartingValues(values, fields = STARTING_VALUE_FIELDS) {
    return Object.fromEntries(fields.map((field) => [field, values[field]]));
}

/** Show a stored value in its input: 0.15 is typed as 15, 9000000 as 9,000,000. */
export function formatStartingValueInput(field, value) {
    if (!Number.isFinite(value)) return '';
    const input = INPUT_BY_FIELD[field];
    const shown = Number((input?.percent ? value * 100 : value).toFixed(6));
    return input?.grouped
        ? shown.toLocaleString('en-US', { maximumFractionDigits: 6 })
        : String(shown);
}

/** A stored value as text, with its unit: 15%, 4%, $9,000,000 or 1. */
export function formatStartingValue(field, value) {
    const shown = formatStartingValueInput(field, value);
    if (!shown) return '';
    if (field === 'target_vol_dollar') return `$${shown}`;
    return INPUT_BY_FIELD[field]?.percent || field === 'target_vol_pct' ? `${shown}%` : shown;
}

/** Fractions compare as the toolbar stores them (eight decimals). */
export function sameFraction(left, right) {
    return Number.isFinite(left)
        && Number.isFinite(right)
        && Number(left.toFixed(8)) === Number(right.toFixed(8));
}

export function startingValueInputs(values) {
    return Object.fromEntries(STARTING_VALUE_FIELDS.map((field) => [
        field,
        formatStartingValueInput(field, values?.[field]),
    ]));
}

/**
 * Read typed inputs back into stored values; a typed value is kept exactly.
 * `values` is set only when every input is valid; `valid` has each valid one.
 */
export function parseStartingValueInputs(inputs) {
    const values = {};
    const errors = {};
    STARTING_VALUE_FIELDS.forEach((field) => {
        const text = String(inputs?.[field] ?? '').replace(/,/g, '').trim();
        const parsed = text === '' ? Number.NaN : Number(text);
        const input = INPUT_BY_FIELD[field];
        if (!Number.isFinite(parsed) || parsed <= 0) {
            errors[field] = 'Enter a number above 0.';
        } else if (input.percent && parsed > 100) {
            errors[field] = 'Enter a percent up to 100.';
        } else {
            values[field] = input.percent ? Number((parsed / 100).toFixed(8)) : parsed;
        }
    });
    return {
        values: Object.keys(errors).length === 0 ? values : null,
        valid: values,
        errors,
    };
}

/** Read a `suggest_pos_sizing_target_vol_dollar` response with the settings it used. */
export function readTargetVolDollarSuggestion(response, basisValues) {
    if (!isPositiveFinite(response?.target_vol_dollar)) {
        throw new Error('The Target Vol ($) suggestion is unavailable.');
    }
    return {
        targetVolDollar: response.target_vol_dollar,
        tradeIdeaScale: isPositiveFinite(response?.trade_idea_scale)
            ? response.trade_idea_scale
            : null,
        basis: pickStartingValues(basisValues, SUGGESTION_BASIS_FIELDS),
    };
}

/**
 * The absolute trade idea most positions stay within at the typed values.
 *
 * A trade idea scales with Target Vol (%) x Scaler / (Max Pos Size x Normal
 * Vol x Target Vol ($)), so the suggestion's scale carries to any typed value.
 */
export function tradeIdeaRangeLimit(suggestion, values) {
    const basis = suggestion?.basis;
    if (!isPositiveFinite(suggestion?.tradeIdeaScale) || !readValues(basis, SUGGESTION_BASIS_FIELDS)) {
        return null;
    }
    if (!readValues(values)) return null;
    const limit = suggestion.tradeIdeaScale
        * (values.target_vol_pct / basis.target_vol_pct)
        * (values.scaler / basis.scaler)
        * (basis.max_pos_size / values.max_pos_size)
        * (basis.normal_vol / values.normal_vol)
        / values.target_vol_dollar;
    return Number.isFinite(limit) ? limit : null;
}

export function formatTradeIdeaRange(limit) {
    if (!Number.isFinite(limit)) return '';
    const shown = limit.toFixed(1);
    return `Most trade ideas will fall between −${shown} and +${shown}`;
}

export function changedTargetDefaults(previous, next) {
    return Object.fromEntries(
        TARGET_DEFAULT_FIELDS
            .filter((field) => previous?.[field] !== next[field])
            .map((field) => [field, next[field]]),
    );
}
